import React, { useId, useState } from "react";
import {
  Box,
  Button,
  ExpandableSection,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  OmsExportPreset,
  OmsPresetScope,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import { OmsConfiguration } from "./omsPresetEditing";
import presetChanges, { PresetChangeValue } from "./omsPresetChanges";
import { comparisonLabel } from "./omsReportFormatting";
import { submittedCondition } from "./omsFilterEditing";

function PresetChangeCell({
  value,
  report,
}: {
  value: PresetChangeValue;
  report: OmsReport;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const { filter } = value;
  const text = filter
    ? comparisonLabel(filter.operator, report.columnTypes?.[filter.column])
    : value.text;
  const values = filter
    ? filter.values || (filter.value !== undefined ? [filter.value] : [])
    : value.values || [];
  const labels = values.map((item) => (item === "" ? '"" (blank)' : item));
  let unit = "column";
  if (filter) unit = "value";
  else if (value.kind === "filters") unit = "filter";
  if (labels.length > 3 || labels.join(", ").length > 160)
    return (
      <SpaceBetween size="xs">
        {text && <span>{text}</span>}
        <Button
          ariaExpanded={expanded}
          ariaControls={detailsId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Hide" : "Show"} {labels.length}{" "}
          {labels.length === 1 ? unit : `${unit}s`}
        </Button>
        <div id={detailsId} hidden={!expanded}>
          {expanded && (
            <ol className="oms-report-download__applied-filters">
              {labels.map((item, index) => (
                // Duplicate values are possible in an ordered filter-field list.
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>{item}</li>
              ))}
            </ol>
          )}
        </div>
      </SpaceBetween>
    );
  return (
    <span>
      {text}
      {text && labels.length > 0 && " "}
      {labels.join(", ")}
    </span>
  );
}

export function OMSPresetContents({
  draft,
  scope,
  report,
  collapsible = true,
}: {
  draft: OmsConfiguration;
  scope: OmsPresetScope;
  report: OmsReport;
  collapsible?: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const allColumns = draft.columns.strategy === "all";
  const names = allColumns ? report.columns : draft.columns.names;
  const selectedLabel = names.length
    ? "Selected columns (CSV order)"
    : "No columns selected";
  const contents = (
    <>
      {/* Keyboard users must be able to scroll the bounded preview. */}
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
      {(!collapsible || expanded) && (
        <div
          className="oms-preset-changes"
          role="region"
          aria-label="Preset settings to save"
          tabIndex={0}
        >
          <SpaceBetween size="m">
            {scope !== "columns" && (
              <section aria-label="Rows to save">
                <Box variant="h3">Rows to export</Box>
                <Box>
                  {draft.mode === "full" ? "All rows" : "Filtered rows"}
                </Box>
                {draft.mode === "filtered" && (
                  <>
                    <Box color="text-body-secondary">
                      {draft.operation === "or"
                        ? "Any condition (OR)"
                        : "All conditions (AND)"}
                    </Box>
                    <ul
                      className="oms-preset-preview__filters"
                      aria-label="Filters to save"
                    >
                      {draft.criteria
                        .filter((criterion) => criterion.column)
                        .map((criterion) => (
                          <li
                            key={criterion.id}
                            className="oms-preset-preview__item"
                          >
                            <Box fontWeight="bold">{criterion.column}</Box>
                            <PresetChangeCell
                              value={{
                                text: "",
                                filter: submittedCondition(criterion),
                              }}
                              report={report}
                            />
                          </li>
                        ))}
                    </ul>
                  </>
                )}
              </section>
            )}
            {scope !== "filters" && (
              <section
                className={
                  scope !== "columns" ? "oms-preset-preview__item" : ""
                }
                aria-label="Columns to save"
              >
                <Box variant="h3">Output columns</Box>
                <PresetChangeCell
                  value={{
                    text: allColumns
                      ? "All columns, including future fields"
                      : selectedLabel,
                    values: names,
                    kind: "columns",
                  }}
                  report={report}
                />
              </section>
            )}
          </SpaceBetween>
        </div>
      )}
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
    </>
  );
  return collapsible ? (
    <ExpandableSection
      headerText="Settings in this preset"
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
    >
      {contents}
    </ExpandableSection>
  ) : (
    contents
  );
}

export default function OMSPresetChanges({
  saved,
  draft,
  name,
  scope,
  report,
}: {
  saved: OmsExportPreset;
  draft: OmsConfiguration;
  name: string;
  scope: OmsPresetScope;
  report: OmsReport;
}) {
  const changes = presetChanges(saved, draft, name, scope);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const detailsId = useId();
  const groups = [
    {
      label: "Settings",
      details: changes.details.filter((change) =>
        ["name", "scope", "rows", "operation"].includes(change.id)
      ),
    },
    {
      label: "Filters",
      details: changes.details.filter((change) =>
        change.id.startsWith("filter-")
      ),
    },
    {
      label: "Columns",
      details: changes.details.filter((change) =>
        change.id.startsWith("column")
      ),
    },
  ].filter((group) => group.details.length);
  return (
    <SpaceBetween size="m">
      {!changes.details.length && (
        <Box color="text-body-secondary">No settings changed.</Box>
      )}
      <div
        id={detailsId}
        className={detailsExpanded ? "oms-preset-changes" : undefined}
        role={detailsExpanded ? "region" : undefined}
        aria-label={
          detailsExpanded ? "Saved and updated preset settings" : undefined
        }
        // Scrollable change evidence needs a keyboard focus target.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={detailsExpanded ? 0 : undefined}
      >
        {groups.map((group) => (
          <section key={group.label} aria-label={group.label}>
            <Box variant="h3">{group.label}</Box>
            {(detailsExpanded ? group.details : group.details.slice(0, 3)).map(
              (change) => (
                <section
                  key={change.id}
                  className="oms-compact-change"
                  aria-label={change.label}
                >
                  {change.id === "columns-order" ? (
                    <>
                      <Box>Order changed</Box>
                      {detailsExpanded && (
                        <div
                          className="oms-compact-change__order"
                          role="region"
                          aria-label="Saved and new column order"
                          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                          tabIndex={0}
                        >
                          <Box fontWeight="bold">Saved order</Box>
                          <ol>
                            {change.before.values?.map((value) => (
                              <li key={value}>{value}</li>
                            ))}
                          </ol>
                          <Box fontWeight="bold">New order</Box>
                          <ol>
                            {change.after.values?.map((value) => (
                              <li key={value}>{value}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <strong>{change.label}: </strong>
                      <PresetChangeCell
                        value={change.before}
                        report={{ ...report, columnTypes: saved.schemaTypes }}
                      />
                      <span aria-label="changed to"> → </span>
                      <PresetChangeCell value={change.after} report={report} />
                    </>
                  )}
                </section>
              )
            )}
            {!detailsExpanded && group.details.length > 3 && (
              <Box color="text-body-secondary">
                {group.details.length - 3} more changes
              </Box>
            )}
          </section>
        ))}
      </div>
      {changes.details.length > 0 && (
        <Button
          iconName={detailsExpanded ? "angle-up" : "angle-down"}
          ariaExpanded={detailsExpanded}
          ariaControls={detailsId}
          onClick={() => setDetailsExpanded(!detailsExpanded)}
        >
          {detailsExpanded ? "Show fewer changes" : "View all changes"}
        </Button>
      )}
    </SpaceBetween>
  );
}
